import React from "react";

import { STATES_INVERTED } from "Constants";
import { format } from "date-fns";
import { ALL_BUSINESS_CATEGORIES_FIELD_VALUES } from "EmployerApplication/Components/Constants";
import NAICSCodeSelectSearch from "EmployerApplication/Components/shoppingCartSection/NAICSCodeSelectSearch";
import { Field, FieldProps } from "react-final-form";
import {
  validateRequired,
  validateCompanyName,
  validateName,
  validatePhone,
  validateDate,
  validateBeforeToday,
  validateZero,
  validateHundred,
  validateFein,
  validateNumber,
  validateEmail,
  validatePassword,
  validateDateEstablished,
  validatePhysicalAddress,
  validateZipCode,
  validateUbi,
  composeValidators,
  escapeHTML,
  parseNonNegativeNumber,
} from "Utils/Validations";

import { Radio } from "Cobalt/RadioGroup";
import { Select } from "Cobalt/Select";
import { TIERED_CONTRIBUTION_ROLES, CONTRIBUTION_TYPES, MULTIPLE_PLAN_CONTRIBUTION_TYPES } from "Common/Constants";
import { SIFinalFormField, SIFormRadioGroup, SIFinalFormRadioGroup, type SIFinalFormRadioGroupProps, SIFinalFormFieldNoCol } from "Common/forms";
import { phoneMask, dateMask, einMask, zipCodeMask, ubiMask } from "Common/forms/masks";
import { Row, Col } from "Common/Grid";
import InfoPopover from "Common/InfoPopover";
import { Select as SIFormSelect } from "Theme/Select";

import SICCodeSelectSearch from "./shoppingCartSection/SICCodeSelectSearch";

const addressGuidance = "If you use a PO Box, you may enter that as the mailing address, but please provide a physical location here.";
const addressValidators = composeValidators(validateRequired, validatePhysicalAddress);

export const BusinessDescription = ({ ...rest }: any) => (
  <SIFinalFormField name="company_application[business_description]" label="Description of Business" type="text" validate={validateRequired} {...rest} />
);

export const CompanyName = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[company_name]"
    label="Company Legal Name"
    guidance="This is the name you would use for your company on tax documents."
    type="text"
    autoFocus
    parse={escapeHTML}
    validate={composeValidators(validateRequired, validateCompanyName)}
    noBounce
    errorRight
    {...rest}
  />
);

export const ContactName = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[contact_name]"
    label="Primary Contact Name"
    type="text"
    validate={composeValidators(validateRequired, validateName)}
    noBounce
    errorRight
    {...rest}
  />
);

export const ContactTitle = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[contact_title]"
    label="Primary Contact Title"
    type="text"
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const ContactPhoneNumber = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[contact_phone_number]"
    label="Phone Number"
    type="text"
    componentProps={{ placeholder: "(555) 555-5555", mask: phoneMask }}
    validate={composeValidators(validateRequired, validatePhone)}
    noBounce
    errorRight
    {...rest}
  />
);

export const PartnerPrimaryPhoneNumber = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[partner_primary_phone_number]"
    label="Primary Contact Phone Number (Optional)"
    guidance="If you would like to be contacted on a different phone number than the one synced from your payroll account, you can provide one here."
    type="text"
    componentProps={{ placeholder: rest.placeholder, mask: phoneMask }}
    validate={validatePhone}
    noBounce
    errorRight
    {...rest}
  />
);

export const PartnerPrimaryEmail = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[partner_primary_email]"
    label="Primary Contact Email (Optional)"
    guidance="If you would like to be contacted on a different email than the one synced from your payroll account, you can provide one here."
    type="email"
    componentProps={{ placeholder: rest.placeholder }}
    validate={validateEmail}
    noBounce
    errorRight
    {...rest}
  />
);

export const CoveringPartTime = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[covering_part_time]"
    label="Offer insurance to part-time employees?"
    guidance="Most companies choose no."
    component={SIFormRadioGroup}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const DateEstablishedAt = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[date_established_at]"
    label="Date Company Established"
    type="text"
    guidance="Can't remember exactly? Just estimate to the nearest month."
    max={new Date()}
    componentProps={{ placeholder: "MM/DD/YYYY", mask: dateMask }}
    validate={composeValidators(validateRequired, validateDate, validateBeforeToday, validateDateEstablished)}
    noBounce
    errorRight
    {...rest}
  />
);

export const PayrollStartDate = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[payroll_start_date]"
    label="Date Company Started Payroll"
    type="text"
    guidance="We require this information for companies established in the past year."
    max={new Date()}
    componentProps={{ placeholder: "MM/DD/YYYY", mask: dateMask }}
    validate={composeValidators(validateDate, validateBeforeToday, validateDateEstablished)}
    noBounce
    errorRight
    {...rest}
  />
);

export const PayrollRunPriorToCutoff = ({ cutoffDate, ...rest }: any) => (
  <SIFinalFormField
    name="company_application[payroll_run_prior_to_cutoff]"
    label={`Have you run payroll prior to ${format(cutoffDate, "MMMM d, yyyy")}?`}
    component={SIFormRadioGroup}
    guidance="We require this information to send proper documents to carriers."
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const DomesticPartnerCoverage = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[domestic_partner_coverage]"
    label="Offer insurance to domestic partners?"
    component={SIFormRadioGroup}
    guidance="Most companies choose yes."
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const TypeOfContribution = ({
  name = "company_application[type_of_contribution]",

  multipleMedicalPlansOffered,
  ...rest
}: SIFinalFormRadioGroupProps & {
  /**
   * Employer offers multiple plans
   * @example Employer App: Determined by # medical plans in cart
   * @example Employer Dashboard: Determined by # medical plans
   * */
  multipleMedicalPlansOffered: boolean;
}) => (
  <SIFinalFormRadioGroup
    name={name}
    label="How will contributions be determined?"
    options={{
      ...(multipleMedicalPlansOffered && {
        [CONTRIBUTION_TYPES.percent_of_base_plan]: MULTIPLE_PLAN_CONTRIBUTION_TYPES.percent_of_base_plan,
      }),
      [CONTRIBUTION_TYPES.percentage]: MULTIPLE_PLAN_CONTRIBUTION_TYPES.percentage,
      [CONTRIBUTION_TYPES.fixed]: MULTIPLE_PLAN_CONTRIBUTION_TYPES.fixed,
    }}
    validate={validateRequired}
    {...rest}
  />
);

export const ContributionMethod = ({ name = "company_application[contribution_method]", ...rest }: any) => (
  <SIFinalFormRadioGroup
    name={name}
    label="Offer the same contribution amount to every employee?"
    options={{
      nonTiered: "Yes",
      tiered: "No",
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const EmployeePercentage = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_contribution[employee_percentage]"
    label="Percentage for Employees"
    componentProps={{
      appendContent: "%",
      type: "number",
      min: 0,
      max: 100,
    }}
    guidance={["Typically ", <strong key="employee-percentage">{">50%"}</strong>, " of the employee's monthly cost."]}
    parse={parseNonNegativeNumber}
    validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
    noBounce
    errorRight
    {...rest}
  />
);

export const DependentPercentage = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_contribution[dependent_percentage]"
    label="Percentage for Dependents"
    componentProps={{
      appendContent: "%",
      type: "number",
      min: 0,
      max: 100,
    }}
    guidance={["Typically ", <strong key="dependent-percentage">0%</strong>, " of the dependent cost. Many companies do not reimburse for dependents."]}
    parse={parseNonNegativeNumber}
    validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
    noBounce
    errorRight
    {...rest}
  />
);

export const EmployeeFixedAmount = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_contribution[employee_fixed_amount]"
    label="Amount for Employees"
    componentProps={{
      prependContent: "$",
      type: "number",
      min: 0,
    }}
    guidance={["Typically ", <strong key="employee-amount">half or more</strong>, " of the employee's monthly cost."]}
    parse={parseNonNegativeNumber}
    validate={composeValidators(validateRequired, validateZero, validateNumber)}
    noBounce
    errorRight
    {...rest}
  />
);
export const ContributionTierRole = ({ name, componentProps = {}, ...rest }: FieldProps<any, any>) => (
  <SIFinalFormField
    name={name}
    p={0}
    mb={0}
    type="select"
    component={Select}
    componentProps={{
      options: Object.entries(TIERED_CONTRIBUTION_ROLES),
      placeholder: "Select a role",
      ...componentProps,
    }}
    validate={validateRequired}
    {...rest}
  />
);

export const DependentFixedAmount = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_contribution[dependent_fixed_amount]"
    label="Amount for Dependents"
    componentProps={{
      prependContent: "$",
      type: "number",
      min: 0,
    }}
    guidance={["Typically ", <strong key="dependent-amount">zero</strong>, ". Many companies do not reimburse for dependents."]}
    parse={parseNonNegativeNumber}
    validate={composeValidators(validateRequired, validateZero, validateNumber)}
    noBounce
    errorRight
    {...rest}
  />
);

export const EmployeeContribution = ({ fieldNamePrefix, type_of_contribution, ...rest }) =>
  type_of_contribution === "fixed" ? (
    <EmployeeFixedAmount name={`${fieldNamePrefix}.employeeFixedAmount`} {...rest} />
  ) : (
    <EmployeePercentage name={`${fieldNamePrefix}.employeePercentage`} {...rest} />
  );
export const DependentContribution = ({ fieldNamePrefix, type_of_contribution, ...rest }) =>
  type_of_contribution === "fixed" ? (
    <DependentFixedAmount name={`${fieldNamePrefix}.dependentFixedAmount`} {...rest} />
  ) : (
    <DependentPercentage name={`${fieldNamePrefix}.dependentPercentage`} {...rest} />
  );

export const Fein = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[fein]"
    label="Federal Tax ID Number"
    type="text"
    guidance="This 9-digit number can be found on your company tax returns and other official documents."
    componentProps={{ placeholder: "FEIN or EIN", mask: einMask }}
    validate={composeValidators(validateRequired, validateFein)}
    noBounce
    errorRight
    {...rest}
  />
);

export const Ubi = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[ubi_number]"
    label="Unified Business Identifier (UBI)"
    type="text"
    guidance="This is a 9-digit number assigned by the Washington Department of Revenue."
    componentProps={{ placeholder: "UBI Number", mask: ubiMask }}
    validate={composeValidators(validateRequired, validateUbi)}
    noBounce
    errorRight
    {...rest}
  />
);

export const OrganizationType = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[organization_type]"
    label="Organization Type"
    component={SIFormSelect}
    componentProps={{
      placeholder: "--------",
      options: {
        "C-Corporation": "C-Corporation",
        "S-Corporation": "S-Corporation",
        Partnership: "Partnership",
        "Sole Proprietorship": "Sole Proprietorship",
        LLC: "LLC",
        Other: "Other",
      },
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const BusinessCategory = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[business_category]"
    label="Business Category"
    guidance="Find the category that most closely matches your business. Don't worry if you can't find an exact match. It won't have an impact on your rates or ability to get approved."
    component={SIFormSelect}
    componentProps={{
      placeholder: "--------",
      options: ALL_BUSINESS_CATEGORIES_FIELD_VALUES,
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const BusinessSubcategory = ({ subCategoryOptions, ...rest }: any) => (
  <SIFinalFormField
    name="company_application[sic_code]"
    label="Business Sub-Category"
    guidance="Find the category that most closely matches your business. Don't worry if you can't find an exact match. It won't have an impact on your rates or ability to get approved."
    component={SIFormSelect}
    componentProps={{
      placeholder: "--------",
      options: subCategoryOptions,
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const PresidentName = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[president_name]"
    label="Owner or President Name"
    type="text"
    validate={composeValidators(validateRequired, validateName)}
    noBounce
    errorRight
    {...rest}
  />
);

export const ProbationaryPeriod = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[probationary_period]"
    label="Waiting Period For New Employees"
    component={SIFormSelect}
    componentProps={{
      options: {
        "1st of month after hire date": "1st of month after hire date",
        "1 month": "1 month",
        "2 months": "2 months",
      },
    }}
    guidance={[
      "Most companies choose ",
      <strong key="waiting-period">'1st of month after hire date'</strong>,
      ". This is the amount of time an employee needs to work before being eligible for insurance benefits.",
    ]}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const LargeGroupMeasurementMethod = ({ disabled }) => {
  const validations = disabled ? false : validateRequired;
  return (
    <Row>
      <Col>
        <label>
          How do you determine which hourly employees are considered full time?
          {disabled && (
            <InfoPopover
              infoText={
                <>
                  <p>Since you are offering part-time employees coverage, all W2 employees will be offered coverage.</p>
                  <p className="text-muted">
                    You will have the option to manually mark certain individuals as ineligible for coverage as you see fit but SimplyInsured will not be
                    monitoring your employees for eligibility.
                  </p>
                </>
              }
            />
          )}
          <Field name="company_application[measurement_method]" type="radio" value="monthly" key="Yes" validate={validations}>
            {({ input }) => (
              <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} disabled={disabled}>
                Monthly Review
                <InfoPopover
                  className="SIFormLabel__Guidance"
                  infoText={
                    <>
                      <p>Monthly Measurement Method is recommended for companies with a stable workforce.</p>
                      <p className="text-muted">Employees must average at least 30 hours per week or 130 hours per month are considered full-time.</p>
                    </>
                  }
                />
              </Radio>
            )}
          </Field>
          <Field name="company_application[measurement_method]" type="radio" value="lookback" key="No" validate={validations}>
            {({ input }) => (
              <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} disabled={disabled}>
                Look-Back 12 Months
                <InfoPopover
                  infoText={
                    <>
                      <p>Look-Back Measurement Method is recommended for companies with variable-hour or seasonal employees.</p>
                      <p className="text-muted">
                        Employee hours are monitored for 12 months. The employee must average at least 30 hours per week or 130 hours per month during that
                        monitoring period to be considered full-time.
                      </p>
                      <p className="text-muted">Eligible employees then enter a stability period where they may remain eligible for 12 months.</p>
                    </>
                  }
                />
              </Radio>
            )}
          </Field>
        </label>
      </Col>
    </Row>
  );
};

export const SameAsMailingAddress = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[same_as_mailing_address]"
    label="Mailing address same as physical address?"
    component={SIFormRadioGroup}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const HasWorkersCompensation = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[has_workers_compensation]"
    label="Does your company have workers' compensation insurance?"
    component={SIFormRadioGroup}
    validate={validateRequired}
    guidance="For health insurance application reasons, if you select no we will assume you are either exempt or will have coverage before your start date."
    noBounce
    errorRight
    {...rest}
  />
);

export const WcCarrier = ({ disabled, ...rest }: any) => {
  const validations = disabled ? false : validateRequired;
  const componentProps = disabled ? { disabled: true, placeholder: "Liberty Mutual, Travelers, etc." } : { placeholder: "Liberty Mutual, Travelers, etc." };
  return (
    <SIFinalFormField
      name="company_application[wc_carrier]"
      label="Current Carrier"
      type="text"
      componentProps={componentProps}
      validate={validations}
      noBounce
      errorRight
      {...rest}
    />
  );
};

type Props = {
  policyStartDateOptions: Record<string, string>;
};

export const CoverageStartDateAt = ({ policyStartDateOptions, ...rest }: Props) => (
  <SIFinalFormField
    name="company_application[coverage_start_date_at]"
    label="When do you want coverage to start?"
    component={SIFormSelect}
    componentProps={{
      placeholder: "Select a date",
      options: policyStartDateOptions,
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const AccountEmail = ({ ...rest }: any) => (
  <SIFinalFormField
    name="account[email]"
    label="Email Address"
    type="email"
    validate={composeValidators(validateEmail, validateRequired)}
    noBounce
    errorRight
    {...rest}
  />
);

export const AccountPassword = ({ ...rest }: any) => (
  <SIFinalFormField
    name="account[password]"
    label="Password"
    componentProps={{ type: "password" }}
    validate={composeValidators(validateRequired, validatePassword)}
    noBounce
    errorRight
    {...rest}
  />
);

export const PhysicalAddress1 = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[physical_address_one]"
    label="Physical Address"
    type="text"
    guidance={addressGuidance}
    componentProps={{ placeholder: "PO Boxes Are Not Allowed" }}
    validate={addressValidators}
    noBounce
    errorRight
    {...rest}
  />
);

export const PhysicalCity = ({ ...rest }: any) => (
  <SIFinalFormField name="company_application[physical_city]" label="City" type="text" validate={validateRequired} noBounce errorRight {...rest} />
);

export const PhysicalState = ({ componentProps, ...rest }: any) => (
  <SIFinalFormField
    name="company_application[physical_state]"
    label="State"
    component={SIFormSelect}
    placeholder="-----"
    componentProps={{ ...componentProps, ...{ options: STATES_INVERTED } }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const PhysicalZip = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[physical_zip]"
    label="Zip Code"
    type="text"
    componentProps={{ mask: zipCodeMask }}
    validate={composeValidators(validateZipCode, validateRequired)}
    noBounce
    errorRight
    {...rest}
  />
);

export const MailAddress1 = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[mail_address_one]"
    label="Mailing Address"
    type="text"
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const MailCity = ({ ...rest }: any) => (
  <SIFinalFormField name="company_application[mail_city]" label="City" type="text" validate={validateRequired} noBounce errorRight {...rest} />
);

export const MailState = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[mail_state]"
    label="State"
    component={SIFormSelect}
    placeholder="-----"
    componentProps={{ options: STATES_INVERTED }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const MailZip = ({ ...rest }: any) => (
  <SIFinalFormField
    name="company_application[mail_zip]"
    label="Zip Code"
    type="text"
    componentProps={{ mask: zipCodeMask }}
    validate={composeValidators(validateZipCode, validateRequired)}
    noBounce
    errorRight
    {...rest}
  />
);

export const SICCodeSearch = ({ shouldPrefillSICCode, isDisability, ...rest }: any) => (
  <SIFinalFormFieldNoCol
    name="company_application[sic_code]"
    component={SICCodeSelectSearch}
    validate={validateRequired}
    componentProps={{ shouldPrefillSICCode, isDisability }}
    {...rest}
  />
);

export const BusinessTenure = ({ ...rest }: any) => (
  <SIFinalFormRadioGroup
    name="company_application[eligible_for_disability]"
    label="Has your company been in business for at least two years?"
    options={{
      Yes: "Yes",
      No: "No",
    }}
    validate={validateRequired}
    noBounce
    errorRight
    {...rest}
  />
);

export const NetSuiteLiabilityAccount = ({ options, ...rest }: any) => (
  <SIFinalFormField
    name="preferences[liability_account_id]"
    label="Liability Account"
    component={SIFormSelect}
    componentProps={{ options }}
    guidance="Select which NetSuite liability account to use for managing Health Insurance Payroll Items"
    validate={validateRequired}
    {...rest}
  />
);

export const NetSuiteExpenseAccount = ({ options, ...rest }: any) => (
  <SIFinalFormField
    name="preferences[expense_account_id]"
    label="Expense Account"
    component={SIFormSelect}
    componentProps={{ options }}
    guidance="Select which NetSuite expense account to use for managing Health Insurance Payroll Items"
    validate={validateRequired}
    {...rest}
  />
);

export const NAICSCodeSearch = ({ shouldPrefillNAICSCode, ...rest }: any) => (
  <SIFinalFormField
    name="company_application[naics_code]"
    label="NAICS Code"
    guidance="Find the category that most closely matches your business. Don't worry if you can't find an exact match. It won't have an impact on your rates or ability to get approved."
    component={NAICSCodeSelectSearch}
    validate={validateRequired}
    componentProps={{ shouldPrefillNAICSCode }}
    noBounce
    errorRight
    {...rest}
  />
);
